import React, { useState, useEffect, useRef } from 'react';
import { ScanFace, Droplet, TrendingUp, Sparkles, Check, ArrowUp, Camera, Maximize2, X, Share2 } from 'lucide-react';

import ShareButton from './ShareButton';

const FACE = 'https://images.unsplash.com/photo-1557296387-5358ad7997bb?fm=jpg&q=85&w=600';

const Bar = ({ label, value, color = '#FF6B4A', delta }) => (
  <div className="mb-2.5">
    <div className="flex items-center justify-between mb-1">
      <span className="text-[11px] font-medium text-[#1A1514]">{label}</span>
      <span className="text-[11px] font-semibold flex items-center gap-1" style={{ color }}>
        {delta != null && <span className="inline-flex items-center text-[#FF6B4A]"><ArrowUp className="w-2.5 h-2.5" />{delta}</span>}
        {value}
      </span>
    </div>
    <div className="h-1.5 w-full rounded-full bg-[#F3E6E0] overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${value}%`, backgroundColor: color, transition: 'width 0.8s ease' }} />
    </div>
  </div>
);

const Ring = ({ value, size = 96, label }) => {
  const stroke = 9;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative mx-auto" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#F3E6E0" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#FF6B4A" strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c - (value / 100) * c}
          style={{ transition: 'stroke-dashoffset 0.9s ease' }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-heading text-2xl font-bold text-[#FF6B4A]">{value}</span>
        {label && <span className="text-[9px] text-[#8C7A76] uppercase tracking-wider">{label}</span>}
      </div>
    </div>
  );
};

const SCENES = [
  {
    title: 'Log your day in seconds',
    desc: 'Track acne, bloating, food, sleep & cycle — the data behind every insight.',
    render: () => (
      <div className="p-4">
        <p className="text-xs font-semibold text-[#8C7A76] uppercase tracking-wider mb-3">Today's Log</p>
        {[['Acne severity', 2, 5], ['Bloating', 1, 5]].map(([l, v, m]) => (
          <div key={l} className="mb-3">
            <span className="text-xs text-[#1A1514]">{l}</span>
            <div className="flex gap-1 mt-1">
              {Array.from({ length: m }).map((_, i) => (
                <span key={i} className={`h-2 flex-1 rounded-full ${i < v ? 'bg-[#A388EE]' : 'bg-[#F3E6E0]'}`} />
              ))}
            </div>
          </div>
        ))}
        <div className="mb-3">
          <span className="text-xs text-[#1A1514]">Water</span>
          <div className="flex gap-1 mt-1">
            {Array.from({ length: 8 }).map((_, i) => (
              <Droplet key={i} className={`w-3.5 h-3.5 ${i < 6 ? 'text-[#FF6B4A] fill-[#FF6B4A]' : 'text-[#E5D0C9]'}`} />
            ))}
          </div>
        </div>
        <div className="mt-4 bg-[#FF6B4A] text-white text-center text-xs font-semibold py-2.5 rounded-full">Save today's log</div>
      </div>
    ),
  },
  {
    title: 'Get your Daily Detox Score',
    desc: 'A clinically-weighted score for inflammation, gut health & hydration.',
    render: () => (
      <div className="p-4 text-center">
        <p className="text-xs font-semibold text-[#8C7A76] uppercase tracking-wider mb-3">Daily Detox Score</p>
        <Ring value={82} size={120} label="/ 100" />
        <div className="mt-4 space-y-2 text-left">
          <Bar label="Inflammation" value={78} color="#A388EE" />
          <Bar label="Gut Health" value={85} color="#4ADE80" />
          <Bar label="Hydration" value={88} color="#FF6B4A" />
        </div>
      </div>
    ),
  },
  {
    title: 'Scan your face with AI',
    desc: 'Point your camera and capture — 30 clinical points analyzed in seconds.',
    render: () => (
      <div className="p-3">
        <div className="face-scan-stage" style={{ borderRadius: '0.75rem' }}>
          <img src={FACE} alt="scan" />
          <div className="face-scan-dim" />
          <div className="face-scan-grid" />
          <div className="face-scan-line" />
          {[[50, 20, '1'], [30, 48, '2'], [70, 48, '3'], [50, 74, '4']].map(([x, y, n]) => (
            <span
              key={n}
              className="absolute -translate-x-1/2 -translate-y-1/2 flex items-center justify-center w-5 h-5 rounded-full text-[9px] font-bold text-white border border-white/70 scan-marker-pop"
              style={{ left: `${x}%`, top: `${y}%`, backgroundColor: '#5BBF7A', animationDelay: `${Number(n) * 0.35}s` }}
            >
              {n}
            </span>
          ))}
          <div className="absolute inset-x-0 bottom-3 text-center px-3">
            <p className="text-[11px] font-semibold text-white">Analyzing 30 clinical points…</p>
          </div>
        </div>
      </div>
    ),
  },
  {
    title: 'See your 30-point clinical report',
    desc: 'Dermatology-grade scores mapped on your photo — skin type, skin age & issues by zone.',
    render: () => (
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <ScanFace className="w-4 h-4 text-[#FF6B4A]" />
          <span className="font-heading text-sm font-bold text-[#1A1514]">AI Skin Diagnostic</span>
        </div>
        <span className="inline-block text-[9px] font-bold uppercase tracking-wider bg-[#FF6B4A] text-white px-2 py-0.5 rounded-full mb-3">
          30-Point Clinical Analysis
        </span>
        <div className="flex items-center gap-3 mb-3">
          <Ring value={85} size={72} />
          <div className="flex flex-col gap-1.5">
            <span className="text-[10px] font-semibold bg-[#FFF2EC] px-2 py-1 rounded-lg text-[#1A1514]">Skin age 26</span>
            <span className="text-[10px] font-semibold bg-[#FFF2EC] px-2 py-1 rounded-lg text-[#1A1514] capitalize">Combination</span>
          </div>
        </div>
        <Bar label="Acne" value={94} />
        <Bar label="Pores" value={80} color="#C99A2E" />
        <Bar label="Hydration" value={83} />
        <Bar label="Dark circles" value={68} color="#A388EE" />
      </div>
    ),
  },
  {
    title: 'Track your glow-up over time',
    desc: 'Before/after deltas show real, measurable progress on every scan.',
    render: () => (
      <div className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-4 h-4 text-[#FF6B4A]" />
          <span className="font-heading text-sm font-bold text-[#1A1514]">Progress since last scan</span>
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          {[['Pores', 12], ['Hydration', 8], ['Redness', 6]].map(([l, d]) => (
            <span key={l} className="inline-flex items-center gap-1 text-[11px] font-semibold bg-[#FF6B4A]/10 text-[#FF6B4A] px-2.5 py-1 rounded-full">
              <ArrowUp className="w-3 h-3" />{l} +{d}
            </span>
          ))}
        </div>
        <Bar label="Overall health" value={86} delta={6} />
        <div className="mt-3 flex items-end gap-1.5 h-16">
          {[58, 64, 70, 75, 82, 86].map((h, i) => (
            <div key={i} className="flex-1 bg-[#4ADE80] rounded-t" style={{ height: `${h}%`, opacity: 0.4 + i * 0.1 }} />
          ))}
        </div>
      </div>
    ),
  },
  {
    title: 'Share your glow-up',
    desc: 'One tap creates an anonymized before/after card — eyes covered, no name.',
    render: () => (
      <div className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Share2 className="w-4 h-4 text-[#FF6B4A]" />
          <span className="font-heading text-sm font-bold text-[#1A1514]">My Skin Progress</span>
        </div>
        <span className="inline-block text-[10px] font-bold bg-[#FF6B4A] text-white px-2.5 py-1 rounded-full mb-3">+12 points</span>
        <div className="grid grid-cols-2 gap-2 mb-3">
          {['BEFORE', 'AFTER'].map((l) => (
            <div key={l} className="relative rounded-xl overflow-hidden h-24">
              <img src={FACE} alt={l} className="w-full h-full object-cover" />
              <div className="absolute left-[6%] right-[6%] rounded" style={{ top: '30%', height: '16%', backgroundColor: '#161b17' }} />
              <span className={`absolute top-1.5 left-1.5 text-[8px] font-bold text-white px-1.5 py-0.5 rounded-full ${l === 'AFTER' ? 'bg-[#FF6B4A]' : 'bg-black/70'}`}>{l}</span>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {['Acne +18', 'Redness +9'].map((p) => (
            <span key={p} className="text-[9px] font-semibold bg-[#FF6B4A]/10 text-[#FF6B4A] px-2 py-0.5 rounded-full">{p}</span>
          ))}
        </div>
        <div className="bg-[#FF6B4A] text-white text-center text-xs font-semibold py-2.5 rounded-full flex items-center justify-center gap-1.5">
          <Share2 className="w-3.5 h-3.5" /> Share to social
        </div>
      </div>
    ),
  },
];

const HowItWorksDemo = ({ onCta }) => {
  const [step, setStep] = useState(0);
  const [paused, setPaused] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (paused) return undefined;
    timer.current = setTimeout(() => setStep((s) => (s + 1) % SCENES.length), 3400);
    return () => clearTimeout(timer.current);
  }, [step, paused]);

  const scene = SCENES[step];

  const phone = (
    <div className="relative w-[280px] h-[572px] rounded-[2.5rem] bg-[#161b17] p-3 shadow-2xl">
      <div className="absolute top-3 left-1/2 -translate-x-1/2 w-28 h-6 bg-[#161b17] rounded-b-2xl z-10" />
      <div className="w-full h-full rounded-[1.9rem] bg-[#FFF2EC] overflow-hidden relative">
        <div className="h-9 bg-white flex items-center justify-center border-b border-[#F3E6E0]">
          <span className="font-heading text-xs font-bold text-[#1A1514]">InnerGlow</span>
        </div>
        <div key={step} className="animate-fade-in">{scene.render()}</div>
      </div>
    </div>
  );

  const dots = (
    <div className="flex items-center justify-center gap-2">
      {SCENES.map((_, i) => (
        <button
          key={i}
          onClick={() => setStep(i)}
          aria-label={`Go to step ${i + 1}`}
          className={`h-2 rounded-full transition-all ${i === step ? 'w-7 bg-[#4ADE80]' : 'w-2 bg-white/30 hover:bg-white/50'}`}
          data-testid={`demo-dot-${i}`}
        />
      ))}
    </div>
  );

  return (
    <section id="how-it-works" className="py-20 bg-white" data-testid="how-it-works-demo">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12 relative">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#4ADE80]/20 text-[#FF6B4A] text-sm font-medium mb-4">
            <Sparkles className="w-4 h-4" /> See it in action
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A1514]">How InnerGlow works</h2>
          <p className="text-base text-[#8C7A76] mt-3 max-w-xl mx-auto">From a quick daily log to a full clinical AI skin report — watch the flow.</p>
          <div className="mt-5 flex items-center justify-center gap-5">
            <button
              onClick={() => setFullscreen(true)}
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#FF6B4A] hover:text-[#E8552F] transition-colors"
              data-testid="demo-fullscreen-btn"
            >
              <Maximize2 className="w-4 h-4" /> Play full screen
            </button>
            <ShareButton label="Share InnerGlow" />
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Phone mockup */}
          <div
            className="flex justify-center order-2 lg:order-1"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >
            {phone}
          </div>

          {/* Captions + steps */}
          <div className="order-1 lg:order-2">
            <div key={`cap-${step}`} className="animate-fade-in mb-8">
              <span className="text-sm font-bold text-[#4ADE80]">Step {step + 1} of {SCENES.length}</span>
              <h3 className="font-heading text-2xl font-bold text-[#1A1514] mt-1 mb-2">{scene.title}</h3>
              <p className="text-base text-[#8C7A76] leading-relaxed">{scene.desc}</p>
            </div>

            <div className="space-y-2 mb-8">
              {SCENES.map((s, i) => (
                <button
                  key={i}
                  onClick={() => { setStep(i); }}
                  className={`w-full text-left flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all ${i === step ? 'bg-[#FF6B4A] text-white' : 'bg-[#FFF2EC] hover:bg-[#F3E6E0] text-[#1A1514]'}`}
                  data-testid={`demo-step-${i}`}
                >
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i === step ? 'bg-white/20' : 'bg-[#4ADE80]/20 text-[#FF6B4A]'}`}>
                    {i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}
                  </span>
                  <span className="text-sm font-medium">{s.title}</span>
                </button>
              ))}
            </div>

            <button
              onClick={onCta}
              className="inline-flex items-center gap-2 bg-[#4ADE80] hover:bg-[#94a37b] text-[#161b17] font-bold rounded-full px-7 py-4 transition-all hover:-translate-y-0.5"
              data-testid="demo-cta-btn"
            >
              <Camera className="w-5 h-5" /> Try your skin scan — $1.99
            </button>
          </div>
        </div>
      </div>

      {/* Full-screen, screen-record-friendly player */}
      {fullscreen && (
        <div className="fixed inset-0 z-[60] bg-[#0f1410] flex flex-col items-center justify-center px-4" data-testid="demo-fullscreen-overlay">
          <button
            onClick={() => setFullscreen(false)}
            className="absolute top-5 right-5 text-white/70 hover:text-white"
            aria-label="Exit full screen"
            data-testid="demo-fullscreen-close"
          >
            <X className="w-7 h-7" />
          </button>
          <p className="text-[#4ADE80] text-sm font-semibold uppercase tracking-wider mb-6">{scene.title}</p>
          {phone}
          <div className="mt-8">{dots}</div>
          <div className="mt-6"><ShareButton variant="dark" label="Share InnerGlow" /></div>
        </div>
      )}
    </section>
  );
};

export default HowItWorksDemo;
